/**
 * Whether this GraphQL error is one a token refresh could fix.
 *
 * Two shapes, because Hasura reports the same situation differently depending
 * on how it is configured to authenticate. JWT mode raises `invalid-jwt` /
 * `Could not verify JWT: JWTExpired` itself. In auth-webhook mode it never
 * verifies the token — the webhook does — and a 401 from the hook comes back
 * as `access-denied` with Hasura's own wording. Matching only the first meant
 * the silent refresh stopped working the moment the env flipped, and every user
 * saw errors for up to a token lifetime instead.
 *
 * Matched on the hook wording rather than on `access-denied` alone,
 * deliberately: an ordinary permission denial carries the same code, and
 * refreshing on those would retry every legitimately forbidden query.
 *
 * A revoked token lands in the second shape too and self-limits: the refresh
 * is refused as well, so `refreshSessionOrReload` resolves false and sends the
 * tab to the login card rather than looping. That depends on the refresh path consulting
 * `auth_revocations`, which #24274 adds and which merges before this.
 *
 * In its own module rather than beside the link that uses it, so the test does
 * not have to pull in Apollo and React: both branches rest on string
 * comparisons against another system's wording, which is exactly the kind of
 * assumption that is silently wrong and only shows up an hour into a session.
 */
export const shouldRetryAfterRefresh = (err: {
  message: string;
  extensions?: { code?: unknown } | null;
}): boolean => {
  const code = err.extensions?.code;
  if (code === 'invalid-jwt') {
    return err.message === 'Could not verify JWT: JWTExpired';
  }
  if (code === 'access-denied') {
    return err.message.includes('Authentication hook unauthorized');
  }
  return false;
};
